/**
 * useApiErrorHandler.js
 *
 * This file defines a custom hook for handling API errors in a React application.
 * It works in conjunction with a NotificationContext to display error messages to the user.
 *
 * The main purposes of this hook are:
 * 1. To provide a consistent way of handling API errors across the application.
 * 2. To automatically display appropriate error messages using the notification system.
 * 3. To handle different types of errors (server errors, network errors, etc.) in a unified manner.
 *
 * Usage:
 * 1. Import this hook in your component:
 *    import { useApiErrorHandler } from '../context/useApiErrorHandler';
 *
 * 2. Use the hook in your component:
 *    const handleApiError = useApiErrorHandler();
 *
 * 3. Use it in your try-catch block when making API calls:
 *    try {
 *      // Your API call here
 *    } catch (error) {
 *      handleApiError(error, setLoading);
 *    }
 *
 * Note: This hook assumes that you have a NotificationContext set up in your application.
 * Make sure that the NotificationProvider is wrapping the relevant part of your component tree.
 */

import { useNotification } from "./useNotification";

/**
 * Custom hook for handling API errors.
 * @returns {Function} A function that handles API errors and displays notifications.
 */
export const useApiErrorHandler = () => {
  const showNotification = useNotification();

  /**
   * Handles API errors and displays appropriate notifications.
   * @param {Error} error - The error object from the API call.
   * @param {Function} [setLoading=null] - Optional function to set loading state.
   */
  return (error, setLoading = null) => {
    // If a setLoading function is provided, set loading to false
    if (setLoading) {
      setLoading(false);
    }

    // Handle errors with a response from the server
    if (error.response) {
      const { status, data } = error.response;

      // Handle validation errors (typically status code 422)
      if (status === 422 && data.errors) {
        let errorMessage = `${data.message}: `;
        Object.entries(data.errors).forEach(([field, messages]) => {
          errorMessage += `${field} - ${messages.join(", ")}. `;
        });
        showNotification("error", errorMessage.trim());
      } else {
        // Handle other types of errors
        showNotification("error", data.message || "An error occurred");
      }
    }
    // Handle errors where the request was made but no response was received
    else if (error.request) {
      showNotification("error", "No response received from server");
    }
    // Handle errors occurring before the request is sent
    else {
      showNotification("error", "An error occurred while sending the request");
    }
  };
};

/**
 * Example usage in a component:
 *
 * import { useApiErrorHandler } from '../context/useApiErrorHandler';
 * import { useState } from 'react';
 * import apiClient from '../api/apiClient';
 *
 * const MyComponent = () => {
 *   const [loading, setLoading] = useState(false);
 *   const handleApiError = useApiErrorHandler();
 *
 *   const fetchData = async () => {
 *     setLoading(true);
 *     try {
 *       const response = await apiClient.get('/some-endpoint');
 *       // Handle successful response
 *       setLoading(false);
 *     } catch (error) {
 *       handleApiError(error, setLoading);
 *     }
 *   };
 *
 *   return (
 *     // Your component JSX
 *   );
 * };
 */
